/**
 * Client-side cron preview.
 *
 * The backend is the authority: it validates on save and it computes the
 * nextRunAt that actually drives firing. This exists only so the form can show
 * "when does this actually run?" while the user types, which is where nearly
 * every misread cron expression gets caught. It intentionally mirrors the
 * backend's five-field grammar; if the two ever disagree, the backend wins and
 * the save will be rejected with the real reason.
 */

export type CronFields = {
  minutes: Set<number>;
  hours: Set<number>;
  daysOfMonth: Set<number>;
  months: Set<number>;
  daysOfWeek: Set<number>;
  dayOfMonthRestricted: boolean;
  dayOfWeekRestricted: boolean;
};

const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const searchLimit = 400000;

function parseValue(token: string, min: number, max: number, names: string[]): number {
  const named = names.indexOf(token.toLowerCase());
  const value = named >= 0 ? named + (names === monthNames ? 1 : 0) : Number(token);

  if (!Number.isInteger(value) || value < min || value > max) {
    throw new Error(`"${token}" is not valid here (expected ${min}-${max})`);
  }
  return value;
}

function parseField(field: string, min: number, max: number, names: string[] = []) {
  const values = new Set<number>();
  let restricted = false;

  for (const part of field.split(',')) {
    if (part === '') {
      throw new Error(`Empty element in "${field}"`);
    }

    const [rangePart, stepPart, ...extra] = part.split('/');
    if (extra.length > 0) {
      throw new Error(`Invalid step in "${field}"`);
    }

    let step = 1;
    if (stepPart !== undefined) {
      step = Number(stepPart);
      if (!Number.isInteger(step) || step < 1) {
        throw new Error(`Invalid step "${stepPart}"`);
      }
    }

    let from: number;
    let to: number;
    if (rangePart === '*') {
      from = min;
      to = max;
      if (step > 1) restricted = true;
    } else {
      restricted = true;
      const bounds = rangePart.split('-');
      if (bounds.length === 1) {
        from = parseValue(bounds[0], min, max, names);
        to = stepPart === undefined ? from : max;
      } else if (bounds.length === 2) {
        from = parseValue(bounds[0], min, max, names);
        to = parseValue(bounds[1], min, max, names);
      } else {
        throw new Error(`Invalid range in "${field}"`);
      }
    }

    if (from > to) {
      throw new Error(`Range ${from}-${to} starts after it ends`);
    }
    for (let value = from; value <= to; value += step) {
      values.add(value);
    }
  }

  return { values, restricted };
}

export function parseCron(expression: string): CronFields {
  const fields = String(expression ?? '').trim().split(/\s+/).filter(Boolean);

  if (fields.length !== 5) {
    throw new Error(`Expected 5 fields (minute hour day-of-month month day-of-week), got ${fields.length}`);
  }

  const minutes = parseField(fields[0], 0, 59);
  const hours = parseField(fields[1], 0, 23);
  const daysOfMonth = parseField(fields[2], 1, 31);
  const months = parseField(fields[3], 1, 12, monthNames);
  const daysOfWeek = parseField(fields[4], 0, 7, dayNames);

  if (daysOfWeek.values.delete(7)) {
    daysOfWeek.values.add(0);
  }

  return {
    minutes: minutes.values,
    hours: hours.values,
    daysOfMonth: daysOfMonth.values,
    months: months.values,
    daysOfWeek: daysOfWeek.values,
    dayOfMonthRestricted: daysOfMonth.restricted,
    dayOfWeekRestricted: daysOfWeek.restricted,
  };
}

/** Minutes that `tz` is ahead of UTC at the given instant. */
function offsetAt(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));

  const get = (type: string) => Number(parts.find(part => part.type === type)?.value);
  // Intl renders midnight as hour 24 in some engines.
  const hour = get('hour') % 24;
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));

  return (asUtc - utcMs) / 60000;
}

/** Wall-clock fields in `tz` (held as a fake-UTC timestamp) to a real instant. */
function wallToInstant(wallMs: number, timeZone: string): number {
  const firstPass = wallMs - offsetAt(wallMs, timeZone) * 60000;
  return wallMs - offsetAt(firstPass, timeZone) * 60000;
}

/** A real instant to the wall-clock fields in `tz`, held as a fake-UTC timestamp. */
function instantToWall(utcMs: number, timeZone: string): number {
  return utcMs + offsetAt(utcMs, timeZone) * 60000;
}

export function nextOccurrences(
  fields: CronFields,
  timeZone: string,
  after: Date,
  count: number
): Date[] {
  const runs: Date[] = [];
  let cursor = after.getTime();

  for (let index = 0; index < count; index++) {
    let wall = Math.floor(instantToWall(cursor, timeZone) / 60000) * 60000 + 60000;
    let found: number | undefined;

    for (let guard = 0; guard < searchLimit && found === undefined; guard++) {
      const date = new Date(wall);
      const month = date.getUTCMonth() + 1;
      const dayOfMonth = date.getUTCDate();

      if (!fields.months.has(month)) {
        wall = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
        continue;
      }

      const domHit = fields.daysOfMonth.has(dayOfMonth);
      const dowHit = fields.daysOfWeek.has(date.getUTCDay());
      const dayMatches =
        fields.dayOfMonthRestricted && fields.dayOfWeekRestricted
          ? domHit || dowHit
          : fields.dayOfMonthRestricted
            ? domHit
            : fields.dayOfWeekRestricted
              ? dowHit
              : true;

      if (!dayMatches) {
        wall = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), dayOfMonth + 1);
        continue;
      }
      if (!fields.hours.has(date.getUTCHours())) {
        wall = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), dayOfMonth, date.getUTCHours() + 1);
        continue;
      }
      if (!fields.minutes.has(date.getUTCMinutes())) {
        wall += 60000;
        continue;
      }

      const instant = wallToInstant(wall, timeZone);
      if (instant > cursor) {
        found = instant;
      } else {
        wall += 60000;
      }
    }

    if (found === undefined) {
      throw new Error('No occurrence within the next four years');
    }

    runs.push(new Date(found));
    cursor = found;
  }

  return runs;
}

export function smallestIntervalMinutes(fields: CronFields, timeZone: string, from: Date): number {
  const runs = nextOccurrences(fields, timeZone, from, 12);
  let smallest = Number.POSITIVE_INFINITY;

  for (let index = 1; index < runs.length; index++) {
    smallest = Math.min(smallest, (runs[index].getTime() - runs[index - 1].getTime()) / 60000);
  }
  return smallest;
}

export function formatInTimeZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export type CronPreview =
  | { ok: true; runs: Date[]; intervalMinutes: number }
  | { ok: false; error: string };

export function previewCron(expression: string, timeZone: string, count = 3): CronPreview {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
  } catch {
    return { ok: false, error: `Unknown timezone "${timeZone}"` };
  }

  try {
    const fields = parseCron(expression);
    return {
      ok: true,
      runs: nextOccurrences(fields, timeZone, new Date(), count),
      intervalMinutes: smallestIntervalMinutes(fields, timeZone, new Date()),
    };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Invalid cron expression' };
  }
}
